import type { OrgFeature } from '@risksmart-app/modules/src/index';
import {
  type FormId,
  getFormConfigRegistry,
} from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormConfig } from '@risksmart-app/shared/forms/types';
import type { VertexBody } from 'digraph-js';
import { DiGraph } from 'digraph-js';
import _ from 'lodash';
import type { FieldValues } from 'react-hook-form';
import type { Helpers } from 'src/pages/custom-datasources/update/display-types/types';

import { processItems } from '../../../../../node_modules/@cloudscape-design/collection-hooks/mjs/operations';
import type { AdditionalConditionField } from '../../edit-field-modal/formRegistryService';
import { getConditionalPropertyFilterProps } from '../../edit-field-modal/formRegistryService';
import { defaultFieldConditionToConditions } from '../convertEditorStateToFormData';
import type {
  CustomisableFormDataContextState,
  TypedFormFieldConfiguration,
} from '../customisable-form-data/CustomisableFormDataContext';

export type ConditionGraph = DiGraph<{
  id: string;
  body: VertexBody;
  adjacentTo: string[];
}>;

export type ConditionalField = Pick<
  TypedFormFieldConfiguration,
  'FieldId' | 'Conditions'
>;

export type ConditionalFields = ConditionalField[];

export type ConditionalSection = {
  SectionId: string;
  Conditions?: TypedFormFieldConfiguration['Conditions'];
};

export type ConditionalSections = ConditionalSection[];

/**
 * Builds a directed graph from field conditions.
 * The function creates a graph where each node represents a field and edges represent
 * conditional dependencies between fields.
 *
 * @returns DiGraph - Graph of all fields and their dependencies
 */
export const buildFieldConditionGraph = (
  fields: ConditionalField[]
): ConditionGraph => {
  // Build adjacency list representation of the dependency graph
  const graph = new DiGraph();

  fields.forEach((field) => {
    graph.addVertices({ id: field.FieldId, adjacentTo: [], body: {} });
  });

  fields.forEach((field) => {
    (field.Conditions?.tokenGroups || []).forEach((tokenGroup) => {
      if ('tokens' in tokenGroup) {
        tokenGroup.tokens.forEach((token) => {
          graph.addEdge({ from: token.propertyKey || '', to: field.FieldId });
        });
      } else {
        graph.addEdge({
          from: tokenGroup.propertyKey || '',
          to: field.FieldId,
        });
      }
    });
  });

  return graph;
};

/**
 * Returns the passed fieldId, and all of its ancestors
 */
export const getAncestorsAndSelf = (
  fieldGraph: ConditionGraph,
  fieldId: string
): string[] => [fieldId, ...Array.from(fieldGraph.getDeepParents(fieldId))];

/**
 * Builds the shared inputs that condition evaluation needs: the
 * Cloudscape filtering properties for the form, and a flattened copy of
 * the current values where `CustomAttributeData.*` paths are hoisted to
 * top-level keys (so propertyFilteringQuery tokens like
 * `CustomAttributeData.foo` resolve).
 *
 * `data` and `getStandardFieldLabel` are stubbed because they only feed
 * the rendered token labels — visibility evaluation never reads them.
 */
const buildConditionEvalContext = (params: {
  formId: FormId;
  customAttributeSchema: CustomisableFormDataContextState['customAttributeSchema'];
  currentValues: FieldValues;
  helpers: Helpers;
  enabledFeatures: OrgFeature[];
  additionalConditionFields?: AdditionalConditionField[];
}): {
  filteringProps: ReturnType<typeof getConditionalPropertyFilterProps>;
  visibleFieldValues: FieldValues;
} => {
  const {
    formId,
    customAttributeSchema,
    currentValues,
    helpers,
    enabledFeatures,
    additionalConditionFields,
  } = params;

  const filteringProps = getConditionalPropertyFilterProps({
    formId,
    schema: customAttributeSchema?.Schema,
    uiSchema: customAttributeSchema?.UiSchema,
    data: { departmentTypes: [], users: [], tagTypes: [], userGroups: [] },
    enabledFeatures,
    helpers,
    additionalFields: additionalConditionFields,
    getStandardFieldLabel: () => '',
  });

  const visibleFieldValues: FieldValues = {
    ...currentValues,
    ...(!currentValues.CustomAttributeData
      ? {}
      : Object.entries(currentValues.CustomAttributeData).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [`CustomAttributeData.${key}`]: value,
          }),
          {}
        )),
  };

  return { filteringProps, visibleFieldValues };
};

/**
 * Merges persisted `form_field_configuration` conditions with code-declared
 * `defaultConditions` from the form config registry. The single source of truth
 * for "effective" field conditions — used both to build the dependency graph
 * and to evaluate visibility, so the two never diverge.
 *
 * Gated on the `form_sections` flag: when disabled, returns the persisted
 * conditions verbatim so `defaultConditions` is fully inert and legacy
 * (flag-off) behaviour is untouched.
 *
 * Precedence when the flag is on:
 * - read-only default → always wins (the persisted row, if any, is left
 *   untouched in the DB — ignore-but-retain — but not read here);
 * - editable default → applies only when no persisted condition exists, so a
 *   customer override takes precedence and the default never clobbers it.
 */
export const getEffectiveFieldConditions = (
  formFieldConfigurations: ConditionalFields | null | undefined,
  formConfig: FormConfig | undefined,
  formSectionsEnabled: boolean
): ConditionalFields => {
  const persisted = formFieldConfigurations ?? [];
  if (!formSectionsEnabled || !formConfig) {
    return persisted;
  }

  const byFieldId = new Map<string, ConditionalField>();
  for (const ff of persisted) {
    byFieldId.set(ff.FieldId, ff);
  }

  for (const fieldConfig of Object.values(formConfig)) {
    const defaultCondition = fieldConfig.defaultConditions;
    if (!defaultCondition || defaultCondition.rules.length === 0) {
      continue;
    }

    const existing = byFieldId.get(fieldConfig.fieldId);
    const hasPersistedConditions =
      !!existing?.Conditions && existing.Conditions.tokenGroups.length > 0;

    if (defaultCondition.readOnly || !hasPersistedConditions) {
      byFieldId.set(fieldConfig.fieldId, {
        FieldId: fieldConfig.fieldId,
        Conditions: defaultFieldConditionToConditions(defaultCondition),
      });
    }
  }

  return Array.from(byFieldId.values());
};

/**
 * Retrieves a set of field ids that are conditionally hidden based on the current form values and
 * the visibility of any ancestor fields.
 * @param options - Configuration options for determining hidden fields
 * @returns
 */
export const getHiddenFields = (options: {
  formId: FormId;
  customisableData: CustomisableFormDataContextState;
  fieldConditionGraph: ConditionGraph;

  currentValues: FieldValues;
  helpers: Helpers;
  enabledFeatures: OrgFeature[];
  additionalConditionFields?: AdditionalConditionField[];
  formSectionsEnabled: boolean;
}): Set<string> => {
  const {
    formId,
    customisableData,
    fieldConditionGraph,
    currentValues,
    helpers,
    enabledFeatures,
    additionalConditionFields,
    formSectionsEnabled,
  } = options;
  const { formFieldConfigurations, customAttributeSchema } = customisableData;
  const formConfigRegistry = getFormConfigRegistry(enabledFeatures);
  const formConfig = formConfigRegistry[formId];

  const fieldsWithConditions = getEffectiveFieldConditions(
    formFieldConfigurations,
    formConfig,
    formSectionsEnabled
  ).filter((ff) => ff.Conditions && ff.Conditions.tokenGroups.length > 0);

  const { filteringProps, visibleFieldValues } = buildConditionEvalContext({
    formId,
    customAttributeSchema,
    currentValues,
    helpers,
    enabledFeatures,
    additionalConditionFields,
  });

  // Store IDs of any fields that are hidden because their form sections are hidden
  const hiddenSectionFieldIds = new Set<string>();
  for (const fieldConfig of Object.values(formConfig)) {
    // When the flag is on, a field's `defaultConditions` supersedes the legacy
    // `visibilityControlledByFieldId` path (its synthesized condition is already
    // in `fieldsWithConditions`), so skip it here to avoid double-hiding. With
    // the flag off, `visibilityControlledByFieldId` remains the sole driver.
    if (formSectionsEnabled && fieldConfig.defaultConditions) {
      continue;
    }
    if (
      fieldConfig.visibilityControlledByFieldId &&
      !currentValues[fieldConfig.visibilityControlledByFieldId]
    ) {
      hiddenSectionFieldIds.add(fieldConfig.fieldId);
    }
  }

  // Only evaluate conditions for fields whose sections are visible
  for (const hiddenFieldId of Array.from(hiddenSectionFieldIds)) {
    delete visibleFieldValues[hiddenFieldId];
  }

  const directConditionIsFieldVisible: Record<string, boolean> = {};
  for (const fieldWithCondition of fieldsWithConditions) {
    const filteredItems = processItems(
      [visibleFieldValues],
      {
        propertyFilteringQuery: fieldWithCondition.Conditions ?? undefined,
      },
      {
        propertyFiltering: {
          filteringProperties: filteringProps.filteringProperties,
          defaultQuery: fieldWithCondition.Conditions ?? undefined,
        },
      }
    );

    directConditionIsFieldVisible[fieldWithCondition.FieldId] =
      !!filteredItems.items.length;
  }

  const hiddenBySelfOrAncestors = new Set<string>();

  for (const fieldId of Object.keys(directConditionIsFieldVisible)) {
    const ancestors = getAncestorsAndSelf(fieldConditionGraph, fieldId);
    const anyAncestorsHidden = ancestors.some(
      (ancestor) => directConditionIsFieldVisible[ancestor] === false
    );
    if (anyAncestorsHidden) {
      hiddenBySelfOrAncestors.add(fieldId);
    }
  }

  return hiddenBySelfOrAncestors;
};

/**
 * Builds a unified condition graph including both fields and sections.
 * Sections can only depend on fields, not other sections.
 */
export const buildConditionGraph = (
  fields: ConditionalField[],
  sections: ConditionalSection[]
): ConditionGraph => {
  const graph = buildFieldConditionGraph(fields);

  // Add section nodes and their field dependencies
  sections.forEach((section) => {
    if (section.Conditions && section.Conditions.tokenGroups.length > 0) {
      graph.addVertices({ id: section.SectionId, adjacentTo: [], body: {} });

      section.Conditions.tokenGroups.forEach((tokenGroup) => {
        if ('tokens' in tokenGroup) {
          tokenGroup.tokens.forEach((token) => {
            if (token.propertyKey) {
              graph.addEdge({ from: token.propertyKey, to: section.SectionId });
            }
          });
        } else if (tokenGroup.propertyKey) {
          graph.addEdge({
            from: tokenGroup.propertyKey,
            to: section.SectionId,
          });
        }
      });
    }
  });

  return graph;
};

/**
 * Retrieves a set of section ids that are conditionally hidden based on the current form values.
 * Sections can only have conditions based on fields, not other sections.
 */
export const getHiddenSections = (options: {
  formId: FormId;
  customisableData: CustomisableFormDataContextState;
  currentValues: FieldValues;
  helpers: Helpers;
  enabledFeatures: OrgFeature[];
  additionalConditionFields?: AdditionalConditionField[];
}): Set<string> => {
  const {
    formId,
    customisableData,
    currentValues,
    helpers,
    enabledFeatures,
    additionalConditionFields,
  } = options;
  const { formSections, customAttributeSchema } = customisableData;

  const sectionsWithConditions =
    formSections?.filter(
      (section) =>
        section.Conditions && section.Conditions.tokenGroups.length > 0
    ) ?? [];

  if (sectionsWithConditions.length === 0) {
    return new Set<string>();
  }

  const { filteringProps, visibleFieldValues } = buildConditionEvalContext({
    formId,
    customAttributeSchema,
    currentValues,
    helpers,
    enabledFeatures,
    additionalConditionFields,
  });

  const hiddenSections = new Set<string>();

  for (const section of sectionsWithConditions) {
    const filteredItems = processItems(
      [visibleFieldValues],
      {
        propertyFilteringQuery: section.Conditions ?? undefined,
      },
      {
        propertyFiltering: {
          filteringProperties: filteringProps.filteringProperties,
          defaultQuery: section.Conditions ?? undefined,
        },
      }
    );

    const isVisible = !!filteredItems.items.length;
    if (!isVisible) {
      hiddenSections.add(section.SectionId);
    }
  }

  return hiddenSections;
};
